import BaseBehavior from '../../../framework/base/base';
import { debounce } from '../../../shared/utilities/debounce';
import { screenSmallerThan } from '../../../shared/utilities/screenSize';

const cardWidth = 280;
const cardsGap = 20;
const slideWidth = cardWidth + cardsGap;

const classes = {
	slidesContainer: '.v2-slider__slides',
	slide: '.v2-slider__slide',
	frame: '.v2-slider__frame'
};

function elementWidth(element) {
	return element.getBoundingClientRect().width || element.offsetWidth;
}

export default class SlidingCard extends BaseBehavior {
	constructor(element) {
		super(element);

		this.initDomCache();
		this.prevCtrl = this.dom.navPrev;
		this.nextCtrl = this.dom.navNext;
		this.slidesContainer = this.element.querySelector(classes.slidesContainer);
		this.slideElems = Array.from(this.element.querySelectorAll(classes.slide));
		this.frame = this.element.querySelector(classes.frame);

		this.isLoading = false;
		this.initialIndex = 0;
		this.rewindSpeed = 600;
		if (
			element.dataset.activecardindex &&
			((screenSmallerThan('md') && element.dataset.mobileActiveIndex?.toLowerCase() === 'true') ||
				(!screenSmallerThan('md') && element.dataset.desktopActiveIndex?.toLowerCase() === 'true'))
		) {
			this.initialIndex = +element.dataset.activecardindex;
		}

		this.directionMultiplier = document.dir === 'rtl' ? -1 : 1;

		this.enableMouseEvents = element.dataset.mouseEvent.toLowerCase() === 'true';

		this.setup();
		this.updatePagingsTotalCount();
		this.addEventListeners();
	}

	isFixedLayout() {
		return !this.isMobile && this.element.classList.contains('v2-sliding-card--inactive');
	}

	addEventListeners() {
		this.prevClickHandler = this.prev.bind(this);
		this.nextClickHandler = this.next.bind(this);
		this.addEventListener(this.dom.navPrev, 'click', this.prevClickHandler);
		this.addEventListener(this.dom.navNext, 'click', this.nextClickHandler);

		this.touchmoveHandler = this.onTouchmove.bind(this);
		this.touchendHandler = this.onTouchend.bind(this);

		this.touchstartHandler = this.onTouchstart.bind(this);
		this.resizeEventHandler = this.onResize.bind(this);
		this.frameClickHandler = this.onFrameClick.bind(this);

		this.frame.addEventListener('touchstart', this.touchstartHandler, { passive: true });

		if (this.enableMouseEvents) {
			this.frame.addEventListener('mousedown', this.touchstartHandler);
			this.frame.addEventListener('click', this.frameClickHandler);
		}

		window.addEventListener('resize', this.resizeEventHandler);

		this.subscribe('loadmore/finished', () => {
			this.slidesContainer = this.element.querySelector(classes.slidesContainer);
			this.updateSlidingCtrlsVisibility();
			const currentIndex = this.currentSlideIndex;
			this.setSlidesWidth();
			this.slide(currentIndex);
			this.isLoading = false;
		});
	}

	translate(to, duration = 300, ease = 'ease') {
		if (this.isFixedLayout()) {
			return;
		}
		const style = this.slidesContainer?.style;

		if (style) {
			style.transitionTimingFunction = ease;
			style.transitionDuration = duration + 'ms';
			style.transform = 'translateX(' + to + 'px)';
		}
	}

	setup() {
		this.position = {
			x: this.slidesContainer.offsetLeft,
			y: this.slidesContainer.offsetTop
		};

		this.updateSlidingCtrlsVisibility();

		this.reset();
	}

	prev() {
		if (this.currentSlideIndex === 0) {
			return;
		}

		this.slide(this.currentSlideIndex - 1);
	}

	next() {
		this.slide(this.currentSlideIndex + 1);
	}

	slide(index, slideDur) {
		if (this.isFixedLayout()) {
			return;
		}

		const maxOffset = Math.round(this.slidesWidth - this.frameWidth);

		if (this.prevCtrl) {
			this.prevCtrl.removeAttribute('disabled');
		}
		if (this.nextCtrl) {
			this.nextCtrl.removeAttribute('disabled');
		}

		let slideOffsetLeft = this.slideElems[index].offsetLeft;
		const mobileCorrection = (this.frameWidth - cardWidth) / 2;
		let transformCorrection = 0;
		if (document.dir === 'rtl') {
			slideOffsetLeft = (this.slideElems[index].offsetLeft - (this.frameWidth - slideWidth)) * -1;
		}

		let nextOffset = Math.min(Math.max(slideOffsetLeft * -1, maxOffset * -1), 0);
		let isLastSlideReached = slideOffsetLeft >= maxOffset;
		if (this.isMobile) {
			nextOffset = Math.min(slideOffsetLeft * -1, 0);
			isLastSlideReached = index === this.slideElems.length - 1;
			if (index > 0) {
				transformCorrection = mobileCorrection;
			}
		}

		nextOffset = (nextOffset + transformCorrection) * this.directionMultiplier;

		this.translate(nextOffset, slideDur);
		this.currentSlideIndex = index;

		/**
		 * update the position with the next position
		 */
		this.position.x = nextOffset;

		if (this.prevCtrl && index === 0) {
			this.prevCtrl.setAttribute('disabled', 'disabled');
		}
		if (this.nextCtrl && isLastSlideReached) {
			this.nextCtrl.setAttribute('disabled', 'disabled');
		}
		const isSecondLastSlide = slideOffsetLeft >= maxOffset - slideWidth;
		if (isSecondLastSlide && this.element.querySelector('.load-more') && !this.isLoading) {
			this.isLoading = true;
			this.element.querySelector('.load-more').click();
		}
		this.updatePagingsActiveCount();
	}

	reset() {
		this.isMobile = screenSmallerThan('sm');
		this.setSlidesWidth();

		const index = this.formatInitialIndexForSlider();
		this.slide(index, this.rewindSpeed);
	}

	setSlidesWidth() {
		this.frameWidth = elementWidth(this.frame);
		this.slideElems = Array.from(this.element.querySelectorAll(classes.slide));
		this.slidesWidth = this.slideElems.reduce(function (previousValue, slide) {
			return previousValue + elementWidth(slide);
		}, 0);
	}

	getSlidesVisibleCount() {
		let count = 0;
		let frameWidthLeft = this.frameWidth;
		while (frameWidthLeft >= 0) {
			if (count === 0) {
				frameWidthLeft -= cardWidth;
			} else {
				frameWidthLeft -= cardWidth + cardsGap;
			}
			count++;
		}
		return count;
	}

	formatInitialIndexForSlider() {
		let formattedInitialIndex = this.initialIndex;

		const totalCardsVisible = this.getSlidesVisibleCount();
		const lastSlideIndex = this.slideElems.length - 1;
		const maxInitialIndex = lastSlideIndex - totalCardsVisible + 2;
		if (!this.isMobile && this.initialIndex > maxInitialIndex) {
			formattedInitialIndex = maxInitialIndex;
		}
		return formattedInitialIndex;
	}

	onTouchstart(event) {
		if (this.isFixedLayout()) {
			return;
		}
		const touches = event.touches ? event.touches[0] : event;

		if (this.enableMouseEvents) {
			this.frame.addEventListener('mousemove', this.touchmoveHandler);
			this.frame.addEventListener('mouseup', this.touchendHandler);
			this.frame.addEventListener('mouseleave', this.touchendHandler);
		}

		this.frame.addEventListener('touchmove', this.touchmoveHandler, { passive: true });
		this.frame.addEventListener('touchend', this.touchendHandler);

		const { pageX, pageY } = touches;

		this.touchOffset = {
			x: pageX,
			y: pageY,
			time: Date.now()
		};

		this.isScrolling = undefined;

		this.delta = {};
	}

	onTouchmove(event) {
		const touches = event.touches ? event.touches[0] : event;
		const { pageX, pageY } = touches;

		this.delta = {
			x: pageX - this.touchOffset.x,
			y: pageY - this.touchOffset.y
		};

		if (this.isScrolling === undefined) {
			this.isScrolling = Boolean(
				this.isScrolling || Math.abs(this.delta.x) < Math.abs(this.delta.y)
			);
		}

		if (!this.isScrolling && this.touchOffset) {
			this.translate(this.position.x + this.delta.x, 0, null);
		}
	}

	onTouchend() {
		/**
		 * time between touchstart and touchend in milliseconds
		 * @duration {number}
		 */
		const duration = this.touchOffset ? Date.now() - this.touchOffset.time : undefined;

		/**
		 * is valid if:
		 *
		 * -> swipe attempt time is over 300 ms
		 * and
		 * -> swipe distance is greater than 25px
		 * or
		 * -> swipe distance is more then a third of the swipe area
		 *
		 * @isValidSlide {Boolean}
		 */
		const isValid =
			(Number(duration) < 300 && Math.abs(this.delta.x) > 25) ||
			Math.abs(this.delta.x) > this.frameWidth / 3;

		const cultureAwareDeltaX = this.delta.x * this.directionMultiplier;
		const isOutOfBounds =
			(!this.currentSlideIndex && cultureAwareDeltaX > 0) ||
			(this.currentSlideIndex === this.slideElems.length - 1 && cultureAwareDeltaX < 0);
		const direction = cultureAwareDeltaX < 0;

		if (!this.isScrolling) {
			if (isValid && !isOutOfBounds) {
				direction ? this.next() : this.prev();
			} else {
				this.translate(this.position.x, 200);
			}
		}

		this.touchOffset = undefined;

		/**
		 * remove eventlisteners after swipe attempt
		 */
		this.frame.removeEventListener('touchmove', this.touchmoveHandler);
		this.frame.removeEventListener('touchend', this.touchendHandler);
		this.frame.removeEventListener('mousemove', this.touchmoveHandler);
		this.frame.removeEventListener('mouseup', this.touchendHandler);
		this.frame.removeEventListener('mouseleave', this.touchendHandler);
	}

	onFrameClick(event) {
		if (this.isFixedLayout()) {
			return;
		}
		if (this.delta.x) {
			event.preventDefault();
		}
	}

	onResize() {
		if (this.frameWidth !== elementWidth(this.frame)) {
			this.reset();
			debounce(() => {
				this.updateSlidingCtrlsVisibility();
			}, 300)();
		}
	}

	updateSlidingCtrlsVisibility() {
		if (this.slidesContainer.scrollWidth <= this.slidesContainer.clientWidth) {
			this.dom.navigation?.classList.add('v2-carousel__navigation--hidden');
		} else {
			this.dom.navigation?.classList.remove('v2-carousel__navigation--hidden');
		}
	}

	updatePagingsActiveCount() {
		const activeCountElem = this.dom.pagingActive;
		if (activeCountElem) {
			let activeNum = Number(this.currentSlideIndex) + 1;

			/* activeNum has max value
				of total slides count */
			if (activeNum > this.slideElems.length) {
				activeNum = this.slideElems.length;
			}
			activeCountElem.innerHTML = this.convertNumberToTwoDigitString(activeNum);
		}
	}

	updatePagingsTotalCount() {
		const totalCountElem = this.dom.pagingTotal;
		if (totalCountElem) {
			totalCountElem.innerHTML = this.convertNumberToTwoDigitString(this.slideElems.length);
		}
	}

	/**
	 * @param {number} num
	 */
	convertNumberToTwoDigitString(num) {
		let activeCountStr = String(num);
		if (num > 0 && num < 10) {
			activeCountStr = '0' + activeCountStr;
		}
		return activeCountStr;
	}

	removeEventListeners() {
		this.frame.removeEventListener('touchstart', this.touchstartHandler, { passive: true });
		this.frame.removeEventListener('touchmove', this.touchmoveHandler, { passive: true });
		this.frame.removeEventListener('touchend', this.touchendHandler);
		this.frame.removeEventListener('mousemove', this.touchmoveHandler);
		this.frame.removeEventListener('mousedown', this.touchstartHandler);
		this.frame.removeEventListener('mouseup', this.touchendHandler);
		this.frame.removeEventListener('mouseleave', this.touchendHandler);
		this.frame.removeEventListener('click', this.frameClickHandler);

		window.removeEventListener('resize', this.resizeEventHandler);

		if (this.prevCtrl) {
			this.prevCtrl.removeEventListener('click', this.prevClickHandler);
		}

		if (this.nextCtrl) {
			this.nextCtrl.removeEventListener('click', this.nextClickHandler);
		}
	}

	cleanup() {
		this.removeEventListeners();
	}
}
